import {useEffect, useState, useCallback} from "react";
import "../App.css";
import axios from "axios";
import {ColorModeIcon} from "../context/ColorModeContext.tsx";
import Navbar from "../components/Navbar.tsx";
import {SubmitButton} from "../components/Buttons.tsx";
import {Helmet} from "react-helmet";
import {CircularProgress, Box, Typography, CircularProgressProps, Collapse, IconButton} from "@mui/material";
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import {DefaultButton} from "../components/Buttons.tsx";
import "../CSS/programmes/DataChecker.css";
import CustomTextField from "../components/CustomTextField.tsx";
import {HooverTooltip} from "../components/InfoField.tsx";

// --- 1. Helper Component for the Circular Progress ---
function CircularProgressWithLabel(props: CircularProgressProps & { value: number }) {
  return (
    <Box sx={{position: 'relative', display: 'inline-flex'}}>
      <CircularProgress
        variant="determinate"
        {...props}
        sx={{
          color: "var(--naturGreen)", // Uses your palette green
        }}
        size={40} // Adjust size if needed
      />
      <Box
        sx={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography
          variant="caption"
          component="div"
          sx={{
            color: "var(--font-primary)", // Uses your palette font color
            fontWeight: "bold"
          }}
        >
          {`${Math.round(props.value)}%`}
        </Typography>
      </Box>
    </Box>
  );
}

interface CheckRow {
  description: string;
  group: string;
  task_key: string;
  task_name: string;
  last_finished: string;
  status: "PENDING" | "RUNNING" | "COMPLETED" | "FAILED" | "NEVER_RUN";
  progress: number;
  action_text: string;
  can_run: boolean;
  can_download: boolean;
  is_stuck: boolean;
}

function DataChecker() {
  const backendUrl = import.meta.env.VITE_REACT_APP_BACKEND_URL;

  const [checks, setChecks] = useState<CheckRow[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [expandedGroups, setExpandedGroups] = useState<{ [key: string]: boolean }>({
    "Items": true,
    "Listings": true,
    "Kombiniert": true
  });

  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState<string>("Loading...");

  const isAnyCheckRunning = checks.some(
    c => c.status === 'RUNNING' || c.status === 'PENDING'
  );

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const getAxiosConfig = () => ({
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(
        `${backendUrl}/data_checker/get_dashboard_data/`,
        getAxiosConfig()
      );
      setChecks(response.data.checks);
      setLastSyncTime(response.data.last_sync);
    } catch (error: any) {
      console.error(error);
      if (checks.length === 0) {
        setErrorMessage("Fehler beim Laden der Daten.");
      }
    }
  }, [backendUrl, checks.length]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => {
      fetchData();
    }, 60000); // Refresh every 60 seconds
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRunCheck = async (taskKey: string) => {
    setInfoMessage("Starte Check...");
    setErrorMessage("");
    try {
      await axios.post(
        `${backendUrl}/data_checker/start/`,
        {task_type: taskKey},
        getAxiosConfig()
      );
      setInfoMessage("Check erfolgreich gestartet.");
      fetchData();
      setTimeout(() => setInfoMessage(null), 3000);
    } catch (error) {
      setErrorMessage("Konnte den Check nicht starten.");
    }
  };

  const handleDownload = async (taskKey: string) => {
    try {
      const response = await axios.get(
        `${backendUrl}/data_checker/download/${taskKey}/`,
        {
          ...getAxiosConfig(),
          responseType: 'blob',
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let fileName = `${taskKey}_results.xlsx`;
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch && fileNameMatch.length === 2) fileName = fileNameMatch[1];
      }

      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setErrorMessage("Fehler beim Herunterladen der Datei.");
    }
  };

  const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));
  const handleSyncData = async () => {
    if (isSyncing) return;
    setIsSyncing(true);
    setInfoMessage("Aktualisiere Produkt-Datenbank...");
    setErrorMessage("");

    try {
      // 1. Start the Task
      const startResponse = await axios.post(
        `${backendUrl}/data_checker/sync_product_data/`,
        {},
        getAxiosConfig()
      );
      const taskId = startResponse.data.task_id;
      setInfoMessage("Sync läuft im Hintergrund...");

      // 2. Poll for Completion
      let isTaskFinished = false;

      while (!isTaskFinished) {
        // Wait 15 seconds between checks
        await delay(15000);

        const statusResponse = await axios.get(
          `${backendUrl}/data_checker/get_sync_product_data_task_status/${taskId}/`,
          getAxiosConfig()
        );

        const status = statusResponse.data.status;
        if (status === 'RUNNING') {
          setInfoMessage("Datenbank wird aktualisiert. Dies läuft im Hintergrund (Dauer bis ca. 10 Minuten)!");
        } else if (status === 'SUCCESS') {
          isTaskFinished = true;
          setInfoMessage("Datenbank erfolgreich aktualisiert!");
          fetchData(); // Refresh the Last Sync timestamp
        } else if (status === 'FAILURE' || status === 'REVOKED') {
          isTaskFinished = true;
          alert("Die Aktualisierung ist fehlgeschlagen.");
        }
      }
    } catch (error: any) {
      setErrorMessage(error.response?.data?.detail || "Fehler beim Sync.");
      alert(error.response?.data?.detail || "Fehler beim Sync.");
    } finally {
      setIsSyncing(false);
      setTimeout(() => setInfoMessage(null), 3000);
    }
  };

  // --- Render Helpers ---

  const renderStatusBadge = (status: string) => {
    return <span className={`status-badge badge-${status.toLowerCase()}`}>{status}</span>;
  };

  const renderActionColumn = (row: CheckRow) => {
    // 1. Progress Circle if Running
    if (row.status === "RUNNING") {
      return (
        <div style={{display: "flex", flexDirection: "column", alignItems: "center", gap: "5px"}}>
          {/* Replaced Linear Bar with Circular Progress */}
          <CircularProgressWithLabel value={row.progress}/>

          {row.is_stuck && (
            <span style={{color: "var(--secondary-color)", fontSize: "0.75rem", fontWeight: "bold"}}>
                        Stuck?
                    </span>
          )}
        </div>
      );
    }

    // 2. Error Text if Failed
    if (row.status === "FAILED") {
      return <span style={{color: "var(--secondary-color)", fontWeight: "bold"}}>{row.action_text}</span>;
    }

    // 3. Download Button if Completed
    if (row.can_download) {
      return (
        <DefaultButton
          label="Excel"
          onClick={() => handleDownload(row.task_key)}
          startIcon={<DownloadIcon/>}
          size="small"
          sx={{
            width: 'auto',
            minWidth: 'auto',
            height: '35px',        // Smaller height for table
            padding: '5px 15px',
            fontSize: '0.85rem'
          }}
        />
      );
    }

    // 4. Pending
    if (row.status === "PENDING") {
      return <span style={{color: "var(--font-secondary)", fontStyle: "italic"}}>Warte auf Ausführung...</span>;
    }

    return <span>-</span>;
  };


  const renderDataManagement = () => {
    let buttonLabel = "Daten aktualisieren (CSV)";
    if (isSyncing) buttonLabel = "Wird aktualisiert...";
    else if (isAnyCheckRunning) buttonLabel = "Checks laufen...";

    return (
      <Box sx={{width: "50%", mx: "auto", mt: 4}}>
        <Box sx={{
          mb: 3,
          p: 2,
          border: "1px solid var(--primary-color)",
          borderRadius: "8px",
          backgroundColor: "rgba(0,0,0,0.02)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          width: "100%"
        }}>
          <Box>
            <Typography variant="h6" sx={{color: "var(--naturGreen)", mb: 0.5}}>
              Datenbasis
            </Typography>
            <Typography variant="body2" sx={{color: "var(--font-secondary)"}}>
              Letzte Aktualisierung: <strong>{lastSyncTime}</strong>
            </Typography>
          </Box>

          <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
            {isSyncing && <CircularProgress size={24} sx={{color: "var(--naturGreen)"}}/>}

            <DefaultButton
              label={buttonLabel}
              onClick={handleSyncData}
              isDisabled={isSyncing || isAnyCheckRunning}
              startIcon={<RefreshIcon/>}
              sx={{
                backgroundColor: (isSyncing || isAnyCheckRunning) ? "var(--primary-colorSwitched)" : "var(--naturGreen)",
                color: "var(--font-primarySwitched)",
                '&:hover': {backgroundColor: "var(--naturGreen-dark)", color: "var(--font-primary)"}
              }}
            />
          </Box>
        </Box>
      </Box>
    );
  };
  const renderCheckTable = (title: string, groupFilter: string) => {
    // 1. Filter by Group AND Search Term
    const groupChecks = checks.filter(c =>
      c.group === groupFilter &&
      c.task_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // If searching and no results in this group, hide the group entirely
    if (searchTerm && groupChecks.length === 0) return null;

    const isExpanded = expandedGroups[groupFilter] ?? true;

    return (
      <div style={{
        marginBottom: "20px",
        border: "1px solid var(--primary-color)",
        borderRadius: "8px",
        overflow: "hidden"
      }}>
        {/* Header (Clickable) */}
        <div
          onClick={() => toggleGroup(groupFilter)}
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 20px",
            backgroundColor: "rgba(0,0,0,0.03)",
            cursor: "pointer",
            borderBottom: isExpanded ? "1px solid var(--primary-color)" : "none"
          }}
        >
          <h4 style={{margin: 0, color: "var(--naturGreen)"}}>{title} ({groupChecks.length})</h4>
          <IconButton size="small">
            {isExpanded ? <ArrowUpIcon/> : <ArrowDownIcon/>}
          </IconButton>
        </div>

        {/* Collapsible Body */}
        <Collapse in={isExpanded}>
          <table style={{width: "100%", borderCollapse: "collapse"}}>
            <thead>
            <tr style={{
              borderBottom: "2px solid var(--primary-color)",
              textAlign: "center",
              color: "var(--font-primary)",
              backgroundColor: "var(--background-main)"
            }}>
              <th style={{padding: "10px", width: "30%"}}>Name</th>
              <th style={{padding: "10px", width: "12%"}}>Letztes Update</th>
              <th style={{padding: "10px", width: "15%"}}>Status</th>
              <th style={{padding: "10px", width: "15%"}}>Ergebnis</th>
              <th style={{padding: "10px", width: "15%"}}>Aktion</th>
            </tr>
            </thead>
            <tbody>
            {groupChecks.map((row) => (
              <tr key={row.task_key}
                  style={{borderBottom: "1px solid #eee", textAlign: "center", color: "var(--font-primary)"}}>
                <td style={{padding: "10px"}}>
                  <div style={{display: "flex", flexDirection: "row", alignItems: "center"}}>
                    <div style={{flex: 1, display: "flex", justifyContent: "flex-start"}}>
                      <HooverTooltip content={row.description}/>
                    </div>
                    <div>{row.task_name}</div>
                    <div style={{flex: 1}}/>
                  </div>
                </td>
                <td style={{padding: "10px"}}>{row.last_finished}</td>
                <td style={{padding: "10px"}}>{renderStatusBadge(row.status)}</td>
                <td style={{padding: "10px"}}>{renderActionColumn(row)}</td>
                <td style={{padding: "10px"}}>
                  {row.can_run ? (
                    <SubmitButton
                      label="Start"
                      onClick={() => handleRunCheck(row.task_key)}
                      isDisabled={!row.can_run || isSyncing}
                      size="small"
                      sx={{minWidth: "100px", padding: "5px 15px", height: "35px"}}
                    />
                  ) : (
                    <span style={{color: "var(--font-secondary)"}}>-</span>
                  )}
                </td>
              </tr>
            ))}
            {groupChecks.length === 0 && !searchTerm && (
              <tr>
                <td colSpan={5} style={{padding: "20px", color: "var(--font-secondary)"}}>Keine Checks verfügbar.</td>
              </tr>
            )}
            </tbody>
          </table>
        </Collapse>
      </div>
    );
  };

  const renderDashboard = () => {
    return (
      <div className="StatisticsAnalyzerPage" style={{color: "var(--font-primary)"}}>
        <h3 style={{color: "var(--font-primary)"}}>Datenbank Checks</h3>

        {renderInfoMessage()}
        {renderErrorMessage()}
        {/* Controls Row: Search + Refresh */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
          gap: "20px",
          flexWrap: "wrap"
        }}>

          <CustomTextField
            variant="outlined"
            placeholder="Suche nach Checks..."
            label="Name"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon style={{marginRight: "8px", color: "var(--font-secondary)"}}/>,
              sx: {
                color: "var(--font-primary)",
                '& .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--primary-color)'},
                '&:hover .MuiOutlinedInput-notchedOutline': {borderColor: 'var(--naturGreen)'},
              }
            }}
            sx={{flexGrow: 1, maxWidth: "400px"}}
          />

          <DefaultButton
            label="Aktualisieren"
            onClick={() => {
              setInfoMessage("Aktualisiere...");
              setErrorMessage("");
              fetchData();
              setTimeout(() => setInfoMessage(null), 1000);
            }}
            startIcon={<RefreshIcon/>}
            sx={{
              width: 'auto', minWidth: 'auto', height: '40px', padding: '0 20px',
              backgroundColor: 'transparent', border: '1px solid var(--primary-color)', color: 'var(--font-primary)',
              '&:hover': {backgroundColor: 'rgba(0,0,0,0.05)', borderColor: 'var(--primary-color) !important'}
            }}
          />
        </div>

        {/* Tables Container */}
        <div style={{display: "flex", flexDirection: "column", gap: "10px"}}>
          {renderCheckTable("Artikel-Checks", "Items")}
          {renderCheckTable("Listing-Checks", "Listings")}
          {renderCheckTable("Auftrags-Checks", "Aufträge")}
          {renderDataManagement()}
          {renderCheckTable("Datenbasisabhängige Checks (Aktualisiere Datenbasis vor Ausführung)", "DB_dependent")}
        </div>
      </div>
    );
  };

  const renderErrorMessage = () => {
    if (errorMessage) {
      return <div className="error-message-container"><p>{errorMessage}</p></div>;
    }
    return null;
  };

  const renderInfoMessage = () => {
    if (infoMessage) {
      return <div className="info-message-container"><p>{infoMessage}</p></div>;
    }
    return null;
  };

  return (
    <>
      <Navbar/>
      <Helmet>
        <title>Datenbank-Checks</title>
      </Helmet>
      {renderDashboard()}
      {ColorModeIcon()}
    </>
  );
}

export default DataChecker;